/* ============================================================
 * src/lib/uploadClient.ts
 *
 * Shared browser → S3 → Convex upload helpers. Extracted from
 * screens/content/_bundle.tsx so BOTH the Upload page and the
 * ReplaceFileModal (screens/pending/_bundle.tsx) drive the exact
 * same presign + PUT-with-progress + complete flow without a
 * circular import (content already imports from pending).
 *
 * The three-step flow against the Convex `.convex.site` host:
 *   1. POST /api/upload/url           — create a new "uploading" asset
 *      OR /api/upload/replace-url     — re-key an EXISTING asset, preserving
 *                                       its id / metadata / cuts / schedule refs
 *   2. PUT the bytes to S3            — putWithProgress (real % via XHR)
 *   3. POST /api/upload/complete      — flip to `pending`, kick AWS Batch
 * ============================================================ */
import { env } from './env';
import { deleteFileHandle } from './fileHandleStore';

// Accepted source extensions + size cap — single-sourced for Upload + Replace.
export const SUPPORTED_EXT = ['mp4', 'mov', 'mkv', 'avi', 'webm', 'mpeg', 'mpg'];
export const MAX_BYTES = 10 * 1024 * 1024 * 1024; // 10 GiB — matches v2.
export const PUT_TIMEOUT_MS = 60 * 60 * 1000; // 60 min

export function isSupported(file: File): boolean {
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ext !== undefined && SUPPORTED_EXT.includes(ext);
}

export function fileTitleStem(name: string): string {
  return name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim();
}

// HTTP routes are mounted on the .convex.site host, not the WebSocket
// .convex.cloud host — matches v2's UploadAssetPage URL swap.
export function httpBase(): string {
  return env.CONVEX_URL.replace('.convex.cloud', '.convex.site');
}

/**
 * PUT a file to a presigned S3 URL, reporting integer upload percentage.
 * Hands the live XHR back via `register` so callers can `.abort()` it on
 * unmount / cancel. Rejects with a Spanish, operator-facing message.
 */
export function putWithProgress(
  url: string,
  file: File,
  contentType: string,
  onProgress: (pct: number) => void,
  register?: (xhr: XMLHttpRequest) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.timeout = PUT_TIMEOUT_MS;
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 PUT falló (HTTP ${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Error de red durante la subida a S3.'));
    xhr.ontimeout = () => reject(new Error('La subida a S3 superó el tiempo límite.'));
    xhr.onabort = () => reject(new Error('Subida cancelada.'));
    register?.(xhr);
    xhr.send(file);
  });
}

/* ============================================================
 * Chunked parallel multipart upload (NMW-371 / NMW-372).
 *
 * Large files are split into parts and PUT to per-part presigned
 * S3 URLs with a small concurrency pool, resumable across page
 * reloads via localStorage. Small files keep the single-PUT path
 * above untouched.
 *
 * Wire contract (backend routes on the .convex.site host):
 *   POST /api/upload/mpu/create   { filename, contentType, assetType, size, title? }
 *        -> { assetId, s3Key, uploadId, partSize, partCount, cloudfrontUrl }
 *   POST /api/upload/mpu/part     { s3Key, uploadId, partNumbers:number[] }
 *        -> { urls: { partNumber, url }[] }
 *   POST /api/upload/mpu/complete { assetId, s3Key, uploadId, parts:{partNumber,etag}[] }
 *        -> { success, assetId }
 *   POST /api/upload/mpu/abort    { assetId, s3Key, uploadId } -> { aborted }
 * ============================================================ */

// NMW-372 dark-launch threshold. Files at/above this size go multipart;
// everything below keeps the single-PUT path. TUNABLE: lower this gradually
// (e.g. 32 MiB, then 16 MiB) as the multipart path proves itself in prod.
export const MULTIPART_THRESHOLD_BYTES = 64 * 1024 * 1024; // 64 MiB

const MPU_POOL = 5;          // concurrent part uploads (4–6 is the sane band)
const MPU_PART_RETRIES = 3;  // per-part attempts before giving up
const MPU_PRESIGN_BATCH = 200; // part URLs presigned per /mpu/part round-trip
const MPU_OFFLINE_WAIT_MS = 5 * 60 * 1000; // max single wait for the 'online' event
const MPU_MAX_OFFLINE_WAITS = 24;          // safety cap on consecutive offline waits

interface DonePart { partNumber: number; etag: string }

interface MpuSession {
  assetId: string;
  s3Key: string;
  uploadId: string;
  partSize: number;
  partCount: number;
  cloudfrontUrl: string;
  assetType: string; // resumed session is bound to the type the asset was created with
  // File fingerprint, mirrored into the value so an interrupted upload can be
  // listed and described after a reload (when the File object is gone).
  fileName: string;
  fileSize: number;
  fileLastModified: number;
  parts: DonePart[];
}

/** A resumable upload recovered from localStorage after a reload. */
export interface PendingMpuUpload {
  key: string;
  name: string;
  size: number;
  lastModified: number;
  assetType: string;
  assetId: string;
  partsDone: number;
  partCount: number;
}

export interface UploadMultipartOpts {
  assetType: string;
  title?: string;
  onProgress?: (fraction: number) => void; // 0..1 across the whole file
  signal?: AbortSignal;
  // Surfaced as soon as the asset row exists so the caller can report a
  // failure against it (mirrors the single-PUT flow's hoisted assetId).
  onAssetId?: (assetId: string, cloudfrontUrl: string) => void;
}

// Resume key: filename + size + lastModified uniquely fingerprints the exact
// bytes on disk, so a reload/crash mid-upload can pick the same S3 uploadId
// back up and skip any parts that already have an ETag.
function mpuStorageKey(file: File): string {
  return `mpu:${file.name}:${file.size}:${file.lastModified}`;
}

/** The resume key uniquely identifying a file's bytes — shared by the
 *  localStorage session and the File System Access handle store. */
export function mpuKey(file: File): string {
  return mpuStorageKey(file);
}

function loadMpuSession(file: File): MpuSession | null {
  try {
    const raw = localStorage.getItem(mpuStorageKey(file));
    if (!raw) return null;
    const s = JSON.parse(raw) as MpuSession;
    if (!s || !s.uploadId || !s.s3Key || !s.assetId || !Array.isArray(s.parts)) return null;
    return s;
  } catch { return null; }
}

function saveMpuSession(file: File, s: MpuSession): void {
  try { localStorage.setItem(mpuStorageKey(file), JSON.stringify(s)); } catch { /* quota; non-fatal */ }
}

function clearMpuSession(file: File): void {
  try { localStorage.removeItem(mpuStorageKey(file)); } catch { /* non-fatal */ }
}

/**
 * Resolve when the browser regains connectivity (the `online` event) — so an
 * upload parked mid-part during a network outage auto-continues on reconnect —
 * or after `maxMs` as a fallback so we never hang forever. Rejects if `signal`
 * aborts (navigation/unmount) so a paused upload can unwind cleanly.
 */
function waitForOnline(signal?: AbortSignal, maxMs = MPU_OFFLINE_WAIT_MS): Promise<void> {
  if (navigator.onLine) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      window.removeEventListener('online', onOnline);
      signal?.removeEventListener('abort', onAbort);
      clearTimeout(timer);
    };
    const onOnline = () => { cleanup(); resolve(); };
    const onAbort = () => { cleanup(); reject(new Error('Subida cancelada.')); };
    const timer = setTimeout(() => { cleanup(); resolve(); }, maxMs);
    window.addEventListener('online', onOnline);
    signal?.addEventListener('abort', onAbort);
  });
}

async function mpuFetch<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${httpBase()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(`${path} rechazado (HTTP ${res.status}): ${await res.text()}`);
  return (await res.json()) as T;
}

/**
 * PUT one part blob to its presigned URL via XHR, reporting bytes-loaded for
 * this part through `onLoaded` and returning the ETag S3 hands back (quotes
 * preserved — CompleteMultipartUpload wants the quoted ETag verbatim).
 */
function putPart(
  url: string,
  blob: Blob,
  onLoaded: (loaded: number) => void,
  signal?: AbortSignal,
  register?: (xhr: XMLHttpRequest) => void,
): Promise<string> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) { reject(new Error('Subida cancelada.')); return; }
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.timeout = PUT_TIMEOUT_MS;
    const onAbort = () => { try { xhr.abort(); } catch { /* settled */ } };
    signal?.addEventListener('abort', onAbort);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onLoaded(e.loaded); };
    xhr.onload = () => {
      signal?.removeEventListener('abort', onAbort);
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader('ETag');
        if (!etag) { reject(new Error('S3 no devolvió ETag para la parte.')); return; }
        onLoaded(blob.size);
        resolve(etag);
      } else reject(new Error(`S3 PUT parte falló (HTTP ${xhr.status})`));
    };
    xhr.onerror = () => { signal?.removeEventListener('abort', onAbort); reject(new Error('Error de red durante la subida a S3.')); };
    xhr.ontimeout = () => { signal?.removeEventListener('abort', onAbort); reject(new Error('La subida a S3 superó el tiempo límite.')); };
    xhr.onabort = () => { signal?.removeEventListener('abort', onAbort); reject(new Error('Subida cancelada.')); };
    register?.(xhr);
    xhr.send(blob);
  });
}

/**
 * Drive a chunked, parallel, resumable multipart upload of `file`.
 * Resolves with the created asset id + CloudFront URL once the S3 object is
 * assembled and the ingest workflow has been kicked. Rejects (after aborting
 * the S3 upload on explicit signal-abort) on unrecoverable failure.
 */
export async function uploadMultipart(file: File, opts: UploadMultipartOpts): Promise<{ assetId: string; cloudfrontUrl: string }> {
  const contentType = file.type || 'application/octet-stream';
  const { signal } = opts;

  // 1 — resume an existing session for these exact bytes, else create one.
  let session = loadMpuSession(file);
  if (session && (session.partSize <= 0 || session.partCount <= 0)) session = null;
  // The stored session is bound to the assetId created with its original
  // assetType. If the operator re-adds the same bytes under a different type,
  // discard the session and start fresh (the old S3 multipart upload is swept
  // by the bucket's abort-incomplete lifecycle rule).
  if (session && session.assetType && session.assetType !== opts.assetType) session = null;
  if (!session) {
    const created = await mpuFetch<{
      assetId: string; s3Key: string; uploadId: string;
      partSize: number; partCount: number; cloudfrontUrl: string;
    }>('/api/upload/mpu/create', {
      filename: file.name,
      contentType,
      assetType: opts.assetType,
      size: file.size,
      title: opts.title,
    }, signal);
    session = {
      ...created,
      assetType: opts.assetType,
      fileName: file.name,
      fileSize: file.size,
      fileLastModified: file.lastModified,
      parts: [],
    };
    saveMpuSession(file, session);
  }
  opts.onAssetId?.(session.assetId, session.cloudfrontUrl);

  const { partSize, partCount } = session;

  // Progress is aggregated across all parts by summing bytes: parts already
  // carrying an ETag count as fully uploaded, in-flight parts contribute their
  // live xhr.upload.loaded, and the total is divided by file.size → 0..1.
  const doneNumbers = new Set(session.parts.map(p => p.partNumber));
  const loadedByPart = new Map<number, number>(); // in-flight bytes per part
  for (const p of session.parts) loadedByPart.set(p.partNumber, partSizeFor(p.partNumber, partSize, partCount, file.size));
  const emitProgress = () => {
    if (!opts.onProgress) return;
    let loaded = 0;
    for (const v of loadedByPart.values()) loaded += v;
    opts.onProgress(Math.min(1, file.size === 0 ? 1 : loaded / file.size));
  };
  emitProgress();

  // Remaining part numbers (1-indexed), skipping resumed ones.
  const remaining: number[] = [];
  for (let n = 1; n <= partCount; n++) if (!doneNumbers.has(n)) remaining.push(n);

  // Presigned-URL cache, filled in batches on demand.
  const urlByPart = new Map<number, string>();
  const presignBatch = async (numbers: number[]) => {
    const need = numbers.filter(n => !urlByPart.has(n));
    for (let i = 0; i < need.length; i += MPU_PRESIGN_BATCH) {
      const chunk = need.slice(i, i + MPU_PRESIGN_BATCH);
      const { urls } = await mpuFetch<{ urls: { partNumber: number; url: string }[] }>(
        '/api/upload/mpu/part',
        { s3Key: session!.s3Key, uploadId: session!.uploadId, partNumbers: chunk },
        signal,
      );
      for (const u of urls) urlByPart.set(u.partNumber, u.url);
    }
  };

  const uploadOnePart = async (n: number): Promise<void> => {
    if (!urlByPart.has(n)) await presignBatch([n]);
    const url = urlByPart.get(n);
    if (!url) throw new Error(`Sin URL prefirmada para la parte ${n}.`);
    const start = (n - 1) * partSize;
    const blob = file.slice(start, Math.min(start + partSize, file.size));
    let lastErr: unknown;
    let offlineWaits = 0;
    for (let attempt = 1; attempt <= MPU_PART_RETRIES; attempt++) {
      if (signal?.aborted) throw new Error('Subida cancelada.');
      // If the network is down, park here until it returns (bounded) so a
      // disconnect/reconnect auto-continues this same in-memory upload rather
      // than failing out. Waiting for 'online' does not consume a retry.
      if (!navigator.onLine && offlineWaits < MPU_MAX_OFFLINE_WAITS) {
        offlineWaits++;
        await waitForOnline(signal);
      }
      try {
        const etag = await putPart(url, blob, (loaded) => { loadedByPart.set(n, loaded); emitProgress(); }, signal);
        session!.parts.push({ partNumber: n, etag });
        loadedByPart.set(n, blob.size);
        saveMpuSession(file, session!);
        emitProgress();
        return;
      } catch (e) {
        lastErr = e;
        if (signal?.aborted) throw e;
        // A failure while offline is almost certainly the outage itself: wait
        // for reconnection and retry WITHOUT burning a retry attempt.
        if (!navigator.onLine && offlineWaits < MPU_MAX_OFFLINE_WAITS) {
          offlineWaits++;
          await waitForOnline(signal);
          attempt--;
          continue;
        }
        // backoff: 0.5s, 1s, 2s
        if (attempt < MPU_PART_RETRIES) await new Promise(r => setTimeout(r, 500 * 2 ** (attempt - 1)));
      }
    }
    throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
  };

  // 3 — concurrency pool of MPU_POOL workers draining `remaining`.
  let cursor = 0;
  const worker = async () => {
    for (;;) {
      if (signal?.aborted) throw new Error('Subida cancelada.');
      const idx = cursor++;
      if (idx >= remaining.length) return;
      await uploadOnePart(remaining[idx]);
    }
  };

  try {
    // Pre-sign ALL remaining parts up front (in MPU_PRESIGN_BATCH-sized calls)
    // so the whole upload makes ~ceil(parts / MPU_PRESIGN_BATCH) presign
    // round-trips total instead of one /mpu/part call per part. Presigned URLs
    // live 12h, which comfortably covers the upload; the per-part fallback in
    // uploadOnePart re-signs only if a URL somehow expired mid-flight.
    await presignBatch(remaining);
    await Promise.all(Array.from({ length: Math.min(MPU_POOL, Math.max(1, remaining.length)) }, worker));

    // 4 — complete: assemble the object + kick the ingest workflow.
    const parts = [...session.parts].sort((a, b) => a.partNumber - b.partNumber);
    await mpuFetch('/api/upload/mpu/complete', {
      assetId: session.assetId,
      s3Key: session.s3Key,
      uploadId: session.uploadId,
      parts,
    }, undefined);
    clearMpuSession(file);
    void deleteFileHandle(mpuStorageKey(file)); // upload done → forget the FS handle
    opts.onProgress?.(1);
    return { assetId: session.assetId, cloudfrontUrl: session.cloudfrontUrl };
  } catch (err) {
    // 5 — ANY interruption here is RESUMABLE: a navigation/unmount pause (signal
    // abort), a network outage, or per-part retry exhaustion. We intentionally
    // keep BOTH the localStorage session and the S3 multipart upload intact so
    // re-adding the same file (or an auto-retry after reconnect) continues from
    // the parts already uploaded. We do NOT call /mpu/abort — that would delete
    // the parts we want to resume; genuinely-abandoned uploads are reaped by the
    // bucket's abort-incomplete-multipart lifecycle rule (1 day). Tagging the
    // error `resumable` tells the caller to leave the asset in "uploading"
    // (marking it "failed" would make /mpu/complete reject on the resumed run).
    if (err && typeof err === 'object') (err as { resumable?: boolean }).resumable = true;
    throw err;
  }
}

// Byte length of a given 1-indexed part (the last part is the remainder).
function partSizeFor(n: number, partSize: number, partCount: number, total: number): number {
  if (n < partCount) return partSize;
  const rem = total - (partCount - 1) * partSize;
  return rem > 0 ? rem : partSize;
}

/**
 * Dispatcher: route large files through the resumable multipart driver and
 * small files through the untouched single-PUT flow. Returns the created
 * asset id + CloudFront URL either way.
 *
 * The single-PUT branch is delegated back to the caller via `singlePut` so
 * this module does not need to duplicate the create→PUT→complete UI-status
 * choreography that lives in the screen.
 */
export function shouldUseMultipart(file: File): boolean {
  return file.size >= MULTIPART_THRESHOLD_BYTES;
}

/**
 * Scan localStorage for multipart uploads that were interrupted (a session
 * exists because it was neither completed nor explicitly discarded). Used on
 * mount to offer "re-attach the file to resume" after a reload.
 */
export function listPendingMpuUploads(): PendingMpuUpload[] {
  const out: PendingMpuUpload[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith('mpu:')) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      let s: MpuSession;
      try { s = JSON.parse(raw) as MpuSession; } catch { continue; }
      if (!s || !s.uploadId || !s.fileName || !Array.isArray(s.parts) || !s.partCount) continue;
      out.push({
        key,
        name: s.fileName,
        size: s.fileSize,
        lastModified: s.fileLastModified,
        assetType: s.assetType,
        assetId: s.assetId,
        partsDone: s.parts.length,
        partCount: s.partCount,
      });
    }
  } catch { /* localStorage unavailable */ }
  return out;
}

/** True when `file` is the exact bytes a pending session was created for. */
export function fileMatchesMpuKey(file: File, key: string): boolean {
  return mpuStorageKey(file) === key;
}

/** Forget a pending session (user dismissed it). The orphan S3 upload is swept
 *  by the bucket's abort-incomplete-multipart lifecycle rule. */
export function discardPendingMpuUpload(key: string): void {
  try { localStorage.removeItem(key); } catch { /* non-fatal */ }
}

/**
 * Explicitly CANCEL a multipart upload (per-row cancel button): tear down the
 * S3 multipart upload so its buffered parts are freed immediately, and drop the
 * resume session + FS handle so it does NOT reappear as resumable. No-op if
 * there is no session for this file (single-PUT, or already completed). Distinct
 * from the pause-on-navigation path, which deliberately KEEPS the session.
 */
export async function abortMultipart(file: File): Promise<void> {
  const session = loadMpuSession(file);
  if (!session) return;
  try {
    await mpuFetch('/api/upload/mpu/abort', {
      assetId: session.assetId, s3Key: session.s3Key, uploadId: session.uploadId,
    });
  } catch { /* best-effort; the lifecycle rule is the backstop */ }
  clearMpuSession(file);
  void deleteFileHandle(mpuStorageKey(file));
}
